import {
  ArrowRightIcon,
  MixerHorizontalIcon,
  Pencil1Icon,
} from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { IHospitalDetail } from "../../../../@types/admin/AdminHospitalDetails";
import { IWard } from "../../../../@types/admin/AdminWard";
import { TableSorted } from "../../../../components/TableSorted";
import { HOSPITAL_WARDS_QUERY_KEY } from "../../../../hooks/queries/admin/hospitalDetails";
import { useModal } from "../../../../hooks/useModal";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { RowActions } from "../../Patients/AdminPatients.styles";
import {
  ErrorMessage,
  FieldHint,
  RowAction,
  SectionCaption,
  SectionCard,
  SectionHeader,
  SectionHeading,
  SectionTitle,
  StatusBadge,
  TableLoadingState,
  TableWrapper,
  WardDetailsPanel,
} from "../../shared";
import { TabStack } from "./HospitalDetails.styles";
import { WardCustomFieldsModal } from "./WardCustomFieldsModal";

interface IHospitalWardsTabProps {
  hospital: IHospitalDetail;
  wards: IWard[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Wards tab of the Hospital Details page (EN-5172) — Retool's `#### Wards`
 * container plus its `drawerFrame_WardDetails`.
 *
 * The ward record and its audit trails are the shared `WardDetailsPanel`
 * (EN-5151), which was built for this. Selecting a ward shows it inline rather
 * than in a drawer: the panel embeds `AuditLogPanel`, whose row drill-down needs
 * the global modal slot.
 *
 * ⚠️ The ward's own edit form deliberately posts only `{ id, code, name,
 * groupId, publish }` — see `WardDetailsPanel`. Its feature configuration is
 * edited through the ward measurement types page and the custom-fields dialog
 * here, not by echoing the whole config back.
 */
export function HospitalWardsTab({
  hospital,
  wards,
  isLoading,
  isError,
}: IHospitalWardsTabProps) {
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const [selectedWard, setSelectedWard] = useState<IWard | null>(null);

  const columns: ColumnDef<IWard, string>[] = useMemo(
    () => [
      {
        id: "id",
        header: "Ward ID",
        accessorFn: (ward) => String(ward.id),
        enableSorting: true,
      },
      { accessorKey: "name", header: "Name", enableSorting: true },
      { accessorKey: "code", header: "Code", enableSorting: true },
      {
        // TableSorted dashes a falsy cell value and skips the renderer, so the
        // accessor returns the display string (EN-5129 UI conventions).
        id: "publish",
        header: "State",
        accessorFn: (ward) => (ward.publish ? "Published" : "Unpublished"),
        cell: ({ row }) => (
          <StatusBadge status={row.original.publish ? "active" : "inactive"}>
            {row.original.publish ? "Published" : "Unpublished"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "created",
        header: "Created",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions>
            <RowAction
              label="Edit"
              icon={<Pencil1Icon />}
              onClick={() => {
                setSelectedWard(row.original);
              }}
            />
            <RowAction
              label="Onboarding fields"
              icon={<MixerHorizontalIcon />}
              onClick={() => {
                openModal(
                  <WardCustomFieldsModal
                    ward={row.original}
                    onDone={closeModal}
                  />,
                  {
                    width: "min(760px, 94vw)",
                    maxHeight: "90vh",
                    showCloseButton: true,
                  }
                );
              }}
            />
            <RowAction
              label="Open"
              icon={<ArrowRightIcon />}
              onClick={() => {
                navigate(`/admin/wards/${row.original.id}`);
              }}
            />
          </RowActions>
        ),
      },
    ],
    []
  );

  return (
    <TabStack>
      <SectionCard>
        <SectionHeader>
          <SectionHeading>
            <SectionTitle>{hospital.wardLabelPlural || "Wards"}</SectionTitle>
            <SectionCaption>{wards.length} in this hospital</SectionCaption>
          </SectionHeading>
        </SectionHeader>

        {isError && (
          <ErrorMessage>
            Failed to load this hospital&apos;s wards. Try refreshing the page.
          </ErrorMessage>
        )}

        <TableWrapper refreshKeys={[[HOSPITAL_WARDS_QUERY_KEY]]}>
          <TableSorted
            columns={columns}
            data={wards}
            emptyStateText={
              isLoading ? <TableLoadingState /> : "This hospital has no wards"
            }
            onRowClick={(ward) => setSelectedWard(ward)}
          />
        </TableWrapper>
      </SectionCard>

      {selectedWard ? (
        <SectionCard>
          <WardDetailsPanel wardId={Number(selectedWard.id)} />
        </SectionCard>
      ) : (
        wards.length > 0 && (
          <SectionCard>
            <FieldHint>
              Select a ward above to edit its record and see its change history.
            </FieldHint>
          </SectionCard>
        )
      )}
    </TabStack>
  );
}
